import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    InputAdornment,
    Radio,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useStore } from '../../stores';
import { Driver } from '../../stores/UserStore';

interface DriversSelectorProps {
    onDriverSelect?: (driver: Driver) => void;
    maxHeight?: string | number;
}

const DriversSelector: React.FC<DriversSelectorProps> = observer(({
    onDriverSelect,
    maxHeight = 500
}) => {
    const { userStore } = useStore();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDriver, setSelectedDriver] = useState<number | null>(null);
    const [filteredDrivers, setFilteredDrivers] = useState<Driver[]>([]);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuDriverId, setMenuDriverId] = useState<number | null>(null);


    useEffect(() => {
        userStore.loadDrivers();
    }, [userStore]);


    useEffect(() => {
        const availableDrivers = userStore.availableDrivers;

        if (searchTerm.trim() === '') {
            setFilteredDrivers(availableDrivers);
        } else {
            const filtered = availableDrivers.filter(driver =>
                `${driver.first_name} ${driver.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                driver.cedula.toLowerCase().includes(searchTerm.toLowerCase()) ||
                driver.phone.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredDrivers(filtered);
        }
    }, [searchTerm, userStore.availableDrivers]);

    const getMaxHeight = () => {
        if (isMobile) return '50vh';
        if (isTablet) return '60vh';
        return maxHeight;
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, driverId: number) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuDriverId(driverId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuDriverId(null);
    };

    const handleDriverSelect = (driverId: number) => {
        setSelectedDriver(driverId);

        if (onDriverSelect) {
            const selectedDriverData = userStore.availableDrivers.find(driver => driver.id === driverId);
            if (selectedDriverData) {
                onDriverSelect(selectedDriverData);
            }
        }
    };

    const menuOptions = [
        { label: 'Editar conductor', action: () => console.log('Editar', menuDriverId) },
        { label: 'Ver detalles', action: () => console.log('Ver detalles', menuDriverId) },
        { label: 'Eliminar', action: () => console.log('Eliminar', menuDriverId) },
    ];

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Typography variant="h5" component="h2" fontWeight="500" mb={1}>
                Conductores
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={3}>
                Selecciona uno de los conductores en la lista para continuar con el registro
            </Typography>

            <Box mb={2}>
                <TextField
                    fullWidth
                    placeholder="Buscar conductores"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    variant="outlined"
                    size="small"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: 'white',
                        }
                    }}
                />
            </Box>

            {userStore.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {userStore.error}
                </Alert>
            )}

            <Box
                sx={{
                    maxHeight: getMaxHeight(),
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: '#a8a8a8',
                        },
                    },
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#c1c1c1 #f1f1f1',
                }}
            >
                {userStore.isLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <List sx={{ p: 0 }}>
                            {filteredDrivers.map((driver) => (
                                <ListItem
                                    key={driver.id}
                                    sx={{
                                        borderRadius: 2,
                                        mb: 1,
                                        backgroundColor: selectedDriver === driver.id ? '#EBF3FF' : 'white',
                                        border: '1px solid',
                                        borderColor: selectedDriver === driver.id ? '#dae7fb' : '#f0f0f0',
                                        '&:hover': {
                                            backgroundColor: selectedDriver === driver.id ? '#EBF3FF' : '#f9f9f9',
                                        },
                                        p: 2,
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => handleDriverSelect(driver.id)}
                                    secondaryAction={
                                        <IconButton edge="end" onClick={(e) => handleMenuOpen(e, driver.id)}>
                                            <MoreVertIcon color="action" />
                                        </IconButton>
                                    }
                                >
                                    <Radio
                                        checked={selectedDriver === driver.id}
                                        onChange={() => handleDriverSelect(driver.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                            p: 0.5,
                                            mr: 1,
                                            color: '#c0c0c0',
                                            '&.Mui-checked': {
                                                color: '#1976d2',
                                            }
                                        }}
                                    />
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant="body1"
                                                fontWeight={500}
                                                color={selectedDriver === driver.id ? '#1976d2' : 'text.primary'}
                                            >
                                                {`${driver.first_name} ${driver.last_name}`} - {driver.cedula}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography variant="body2" color="text.secondary">
                                                Tel: {driver.phone} • Registrado: {new Date(driver.created_at).toLocaleDateString('es-ES')}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>

                        {filteredDrivers.length === 0 && !userStore.isLoading && (
                            <Box textAlign="center" py={4}>
                                {searchTerm ? (
                                    <Typography variant="body1" color="text.secondary">
                                        No se encontraron conductores con "{searchTerm}"
                                    </Typography>
                                ) : (
                                    <Typography variant="body1" color="text.secondary">
                                        No hay conductores disponibles
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                        borderRadius: 2,
                        minWidth: 180,
                    }
                }}
            >
                {menuOptions.map((option, index) => (
                    <MenuItem
                        key={index}
                        onClick={() => {
                            option.action();
                            handleMenuClose();
                        }}
                        sx={{ py: 1 }}
                    >
                        <Typography variant="body2">
                            {option.label}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        </Paper>
    );
});

export default DriversSelector;
